import { useState } from 'react';

import { createTag, updateTag } from '../../api/business/endpoints';
import type { Tag, TextChanges } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { LocalisedTextField } from '../../components/LocalisedTextField';
import { textChanges, textsOf } from '../../components/localisedText';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useT } from '../../i18n/LanguageContext';

type TagDialogProps = {
  companyId: string;
  /** null creates a tag; a tag renames it. */
  tag: Tag | null;
  onClose: () => void;
};

/** A tag's name in every content language — unique in the company per language; the API says so when it is not. */
export const TagDialog = ({ companyId, tag, onClose }: TagDialogProps) => {
  const t = useT();
  const { languages, available } = useContentLanguages();
  const original = tag ? textsOf(tag.translations, 'name', languages) : null;
  const [names, setNames] = useState<Record<string, string>>(original ?? {});

  const mutation = useAppMutation(
    (changes: TextChanges) => (tag ? updateTag(companyId, tag.id, changes) : createTag(companyId, changes)),
    ['tags', 'product', 'products'],
    onClose,
  );

  return (
    <FormDialog
      title={tag ? t('tags.edit') : t('tags.create')}
      onClose={onClose}
      onSubmit={() => mutation.mutate(textChanges(names, original, languages))}
      submitLabel={tag ? t('common.save') : t('common.create')}
      isBusy={mutation.isPending || (names[languages[0]] ?? '').trim() === ''}
      error={mutation.error}
      maxWidth="xs"
    >
      <LocalisedTextField
        autoFocus
        label={t('tags.name')}
        languages={languages}
        available={available}
        values={names}
        onChange={setNames}
        maxLength={80}
      />
    </FormDialog>
  );
};
